import {
  menu,
  type MenuConfig,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { PropertyConfigMenuContext } from '@blocksuite/data-view';
import { LoadingIcon } from '@blocksuite/icons/lit';
import type { Doc } from '@blocksuite/store';

import { EditorHostKey } from '../../context/host-context.js';
import {
  collectAllDatabases,
  collectDatabasesFromDoc,
  collectUnloadedDocs,
  type DatabaseTargetCandidate,
  databaseTitleOf,
  docTitleOf,
  loadDocs,
} from '../../utils/collect-databases.js';
import { ensurePairColumn, removePairColumn } from './pairing.js';
import type { RelationPropertyData } from './types.js';

type RelationMenuContext = {
  property: PropertyConfigMenuContext<RelationPropertyData>['property'];
  std: NonNullable<ReturnType<typeof getStd>>;
};

const findOwnerDatabase = (
  std: RelationMenuContext['std'],
  propertyId: string
): DatabaseBlockModel | null => {
  if (!std) return null;
  const databases = std.store.getModelsByFlavour(
    'affine:database'
  ) as DatabaseBlockModel[];
  for (const db of databases) {
    if (db.props.columns.some(c => c.id === propertyId)) return db;
  }
  return null;
};

const getStd = (
  property: PropertyConfigMenuContext<RelationPropertyData>['property']
) => property.view.serviceGet(EditorHostKey)?.std;

/**
 * Apply a target-database selection: detach the previous pair column
 * (if any) and wire the new one via ensurePairColumn.
 */
const applyTargetSelection = (
  ctx: RelationMenuContext,
  candidate: DatabaseTargetCandidate
): void => {
  const { property, std } = ctx;
  const workspace = std.workspace;
  const data = property.data$.value;
  const sourceDb = findOwnerDatabase(std, property.id);
  const sourceDocId = std.store.id;

  const targetDoc = workspace.getDoc(candidate.docId);
  if (!targetDoc || !targetDoc.loaded) return;
  const targetDb = collectDatabasesFromDoc(targetDoc).find(
    d => d.id === candidate.blockId
  );
  if (!targetDb) return;

  // Detach old pair if present.
  if (
    sourceDb &&
    data.pairedColumnId &&
    (data.targetDocId !== candidate.docId ||
      data.targetBlockId !== candidate.blockId)
  ) {
    const oldTargetDoc = workspace.getDoc(data.targetDocId ?? sourceDocId);
    if (oldTargetDoc?.loaded && data.targetBlockId) {
      const oldDb = oldTargetDoc
        .getStore({ id: oldTargetDoc.id })
        .getBlock(data.targetBlockId)?.model as unknown as
        | DatabaseBlockModel
        | undefined;
      if (oldDb) removePairColumn(oldDb, data.pairedColumnId);
    }
  }

  const pairedColumnId = sourceDb
    ? ensurePairColumn({
        sourceDocId,
        sourceBlockId: sourceDb.id,
        sourceColumnId: property.id,
        sourceColumnName: property.name$.value ?? 'Related',
        backRelationName: data.backRelationName,
        targetDb,
        existingPairedColumnId: data.pairedColumnId,
      })
    : null;

  property.dataUpdate(d => ({
    ...d,
    targetDocId: candidate.docId,
    targetBlockId: candidate.blockId,
    pairedColumnId,
  }));
};

/**
 * Build the items of the "Target database" menu. Re-invoked after lazy
 * doc loading to produce the refreshed list (self-recursive via reopen).
 */
const buildTargetMenuItems = (ctx: RelationMenuContext): MenuConfig[] => {
  const { property, std } = ctx;
  const workspace = std.workspace;
  const data = property.data$.value;
  const candidates = collectAllDatabases(workspace);

  const reopen = (anchor: HTMLElement) => {
    popMenu(popupTargetFromElement(anchor), {
      options: {
        title: { text: 'Target database' },
        items: buildTargetMenuItems(ctx),
      },
    });
  };

  const candidateActions = candidates.map(c =>
    menu.action({
      name: `${c.docTitle} / ${c.blockTitle}`,
      isSelected:
        data.targetDocId === c.docId && data.targetBlockId === c.blockId,
      select: () => {
        applyTargetSelection(ctx, c);
      },
    })
  );

  const unloadedDocs = collectUnloadedDocs(workspace);

  // (a) List not-yet-loaded docs; selecting one loads it on demand and
  // (b) re-triggers the selection once the block tree is available.
  const loadOneDoc = async (ele: HTMLElement, metaId: string) => {
    const doc = workspace.getDoc(metaId);
    if (!doc) return;
    await loadDocs([doc]);
    const dbs = collectDatabasesFromDoc(doc);
    if (dbs.length === 1) {
      const db = dbs[0];
      if (db) {
        applyTargetSelection(ctx, {
          docId: doc.id,
          docTitle: docTitleOf(workspace, doc.id),
          blockId: db.id,
          blockTitle: databaseTitleOf(db),
        });
        return;
      }
    }
    // Multiple or zero databases — show the refreshed list.
    reopen(ele);
  };

  const loadAllDocs = async (ele: HTMLElement) => {
    const docs = unloadedDocs
      .map(m => workspace.getDoc(m.id))
      .filter((d): d is Doc => Boolean(d));
    await loadDocs(docs);
    reopen(ele);
  };

  const loadDocActions =
    unloadedDocs.length <= 8
      ? unloadedDocs.map(meta =>
          menu.action({
            name: `Load “${docTitleOf(workspace, meta.id)}”…`,
            prefix: LoadingIcon(),
            select: (ele: HTMLElement) => {
              loadOneDoc(ele, meta.id).catch(err =>
                console.warn(
                  '[relation-target-picker] failed to load doc',
                  meta.id,
                  err
                )
              );
            },
          })
        )
      : [];

  // (c) Load all remaining docs, then reopen the menu with the new list.
  const loadAllAction =
    unloadedDocs.length > 0
      ? menu.action({
          name: `Load ${unloadedDocs.length} more docs…`,
          prefix: LoadingIcon(),
          select: (ele: HTMLElement) => {
            loadAllDocs(ele).catch(err =>
              console.warn('[relation-target-picker] failed to load docs', err)
            );
          },
        })
      : null;

  const currentTargetLabel = (() => {
    if (!data.targetBlockId) return null;
    const found = candidates.find(
      c => c.blockId === data.targetBlockId && c.docId === data.targetDocId
    );
    return found ? `${found.docTitle} / ${found.blockTitle}` : 'Unknown target';
  })();

  const items: MenuConfig[] = [];
  items.push(
    ...(candidateActions.length === 0
      ? [
          menu.action({
            name: 'No databases available',
            select: () => {
              /* noop */
            },
          }),
        ]
      : candidateActions),
    ...loadDocActions,
    ...(loadAllAction ? [loadAllAction] : []),
    ...(currentTargetLabel
      ? [
          menu.action({
            name: `Clear (current: ${currentTargetLabel})`,
            select: () => {
              if (data.pairedColumnId && data.targetDocId) {
                const oldDoc = workspace.getDoc(data.targetDocId);
                if (oldDoc?.loaded && data.targetBlockId) {
                  const oldDb = oldDoc
                    .getStore({ id: oldDoc.id })
                    .getBlock(data.targetBlockId)?.model as unknown as
                    | DatabaseBlockModel
                    | undefined;
                  if (oldDb) removePairColumn(oldDb, data.pairedColumnId);
                }
              }
              property.dataUpdate(d => ({
                ...d,
                targetDocId: null,
                targetBlockId: null,
                pairedColumnId: null,
              }));
            },
          }),
        ]
      : [])
  );
  return items;
};

export const buildRelationConfigMenu = (
  ctx: PropertyConfigMenuContext<RelationPropertyData>
): MenuConfig[] => {
  const { property } = ctx;
  const std = getStd(property);
  if (!std) return [];

  const menuCtx: RelationMenuContext = { property, std };
  const data = property.data$.value;

  return [
    menu.input({
      placeholder: 'Back-relation name (used when creating pair)',
      initialValue:
        data.backRelationName ?? `← ${property.name$.value ?? 'Related'}`,
      onBlur: text => {
        property.dataUpdate(d => ({
          ...d,
          backRelationName: text,
        }));
      },
    }),
    menu.action({
      name: data.singleRef ? 'Limit: single page' : 'Limit: multiple pages',
      hide: () => property.type$.value !== 'orghub-relation',
      select: () => {
        property.dataUpdate(d => ({
          ...d,
          singleRef: !data.singleRef,
        }));
      },
    }),
    menu.subMenu({
      name: 'Target database',
      hide: () => property.type$.value !== 'orghub-relation',
      options: {
        items: buildTargetMenuItems(menuCtx),
      },
    }),
  ];
};
